import Header from "../../../shared/common/Header";
import Security from "../components/settings/Security";
import ThemeMode from "../components/settings/ThemeMode";
import { motion } from "framer-motion";
import SyntheticGrassSelector from "@shared/common/SyntheticGrassSelector";
import { useState } from "react";
import UpdateTrackingTokenModal from "../components/settings/UpdateTrackingTokenModal";
import UsersManager from "../components/settings/UsersManager";
import FootbarLabSection from "../components/settings/FootbarLabSection";
import { IS_DEV } from "../App";
import { useTheme } from '@shared/common/ThemeContext';
import { useMe } from "@shared/common/useMe";
import { useTranslation } from "react-i18next";
import { hasRestrictedClubUserRole } from "@shared/common/roleAccess";
import { Link } from "react-router-dom";
import { ClipboardList } from "lucide-react";

const SettingsPage = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { me, loading, err } = useMe();
  const club_id = me?.club_id || localStorage.getItem("club_id") || localStorage.getItem("user_id");
  const hasLimitedAccess = !me || hasRestrictedClubUserRole(me);

  const wrapperCls = `flex-1 overflow-auto relative z-10 ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"}`;
  const cardCls = isDarkMode
    ? "bg-gray-800 text-gray-100"
    : "bg-white text-gray-800";

  return (
    <div className={wrapperCls}>
        <Header title={t("pages.settings.title")} isDarkMode={isDarkMode} />
      <main className="max-w-4xl mx-auto py-6 px-4 lg:px-8">
          <Security />
          <ThemeMode />

        {!hasLimitedAccess && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="mb-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
            >
              {t("pages.settings.tracking_token")}
            </button>
        )}

        <UpdateTrackingTokenModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          clubId={club_id}
        />

        {/* ⬇️ Ancien “type of synthetic pitch” mis en pause */}
        {/*
        <SyntheticGrassSelector clubId={localStorage.getItem("user_id")} />
        */}

        {/* Bloc Survey (Hopper) */}
        <motion.section
          className={`
            mt-6 relative overflow-hidden rounded-2xl border p-4 sm:p-5
            ${cardCls} ${isDarkMode ? "border-gray-700" : "border-gray-200"}
          `}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22 }}
        >
          {/* Accent à gauche */}
          <div
            className={`
              absolute inset-y-3 left-2 w-1 rounded-full
              ${isDarkMode ? "bg-emerald-400/70" : "bg-emerald-500/80"}
            `}
          />

          <div className="pl-4 sm:pl-5 relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            {/* Titre + description */}
            <div className="flex items-start gap-3">
              <div
                className={`
                  mt-1 flex h-9 w-9 items-center justify-center rounded-full
                  ${isDarkMode ? "bg-emerald-500/15" : "bg-emerald-500/10"}
                `}
              >
                <ClipboardList
                  className={isDarkMode ? "text-emerald-300" : "text-emerald-600"}
                  size={18}
                />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold">{t("pages.settings.survey_title")}</h3>
                  <span
                    className={`
                      inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide
                      ${isDarkMode
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-100"}
                    `}
                  >
                    Hopper
                  </span>
                </div>
                <p
                  className={`
                    text-xs sm:text-sm leading-snug
                    ${isDarkMode ? "text-gray-400" : "text-gray-600"}
                  `}
                >
                  {t("pages.settings.survey_body")}{" "}
                  <span className={isDarkMode ? "text-gray-300" : "text-gray-800"}>
                    {t("pages.settings.survey_body_extra")}
                  </span>
                </p>
              </div>
            </div>

            {/* Bouton d’accès */}
            <motion.div
              whileHover={{ scale: 1.02, translateY: -1 }}
              whileTap={{ scale: 0.98, translateY: 0 }}
              className="self-start sm:self-center"
            >
              <Link
                to="/survey"
                className={`
                  inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm font-medium
                  shadow-sm
                  ${isDarkMode
                    ? "bg-emerald-500 text-gray-900 hover:bg-emerald-400"
                    : "bg-emerald-500 text-white hover:bg-emerald-600"}
                  transition-colors
                `}
              >
                <span>{t("pages.settings.open_survey")}</span>
              </Link>
            </motion.div>
          </div>
        </motion.section>

        {IS_DEV && <FootbarLabSection />}

        {IS_DEV && (
          loading ? (
          <p className="opacity-70 mt-6">{t("pages.settings.loading_permissions")}</p>
        ) : err ? (
          <p className="text-red-500 text-sm mt-6">
            {t("pages.settings.permissions_error", { error: err })}
          </p>
        ) : me?.roles?.includes("admin") ? (
            <UsersManager clubId={club_id} />
        ) : (
          <section
            className={`mt-6 rounded-2xl border p-4 ${cardCls} ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <h3 className="font-semibold mb-1">{t("pages.settings.users_title")}</h3>
            <p className="text-sm opacity-80">
              {t("pages.settings.users_denied")}
            </p>
          </section>
          )
        )}
      </main>
    </div>
  );
};

export default SettingsPage;
